/**
 * The date rules behind the "schedule removal" dialog.
 *
 * <p>
 * Kept as pure functions rather than inline in the component because they are the part that is
 * easy to get wrong and impossible to see: everything here is a calendar date <b>at the office</b>,
 * which is not the browser's calendar. Formatting a date through `Date` and reading it back is how
 * a person entered the 1st and the server was sent the 30th — an off-by-one nobody notices until
 * somebody loses access a day early.
 * </p>
 *
 * <p>
 * So dates are handled as `yyyy-MM-dd` strings throughout, which is what `<input type="date">`
 * produces and what the API expects, and never converted to a timestamp on the way.
 * </p>
 */

/** The date the server says it is at the office, and the boundary it judges dates against. */
export interface AccessEndBounds {
  /** Today at the office, `yyyy-MM-dd`. Nothing before it may be scheduled. */
  readonly today: string;
  /** Today + the office's fill-in period. Dates before it disrupt bookings that already exist. */
  readonly earliestOutsideBookingWindow: string;
}

/** Why a chosen date cannot be submitted as it stands, or undefined when it can. */
export type AccessEndProblem = 'empty' | 'in-the-past';

export const accessEndProblem = (chosen: string, bounds: AccessEndBounds): AccessEndProblem | undefined => {
  if (chosen === '') {
    return 'empty';
  }

  // String comparison is exact for yyyy-MM-dd and needs no timezone at all, which is the reason
  // the format is carried this far rather than parsed at the edge.
  if (chosen < bounds.today) {
    return 'in-the-past';
  }

  return undefined;
};

/**
 * Whether the chosen date falls inside the period colleagues can still book.
 *
 * <p>
 * Not an error. For a person this is the ordinary case — a last working day is usually inside the
 * window — but it is the case where bookings that already exist get cancelled, so the dialog says
 * so and the request carries the override.
 * </p>
 */
export const isInsideBookingWindow = (chosen: string, bounds: AccessEndBounds): boolean =>
  chosen !== '' && chosen < bounds.earliestOutsideBookingWindow;

/**
 * The day after a last working day, which is what the field actually holds.
 *
 * <p>
 * Administrators think in last working days; the column holds the first day without access. Doing
 * that arithmetic in the client keeps the two ideas apart on screen instead of asking somebody to
 * remember to add one — a mistake that costs the leaver a day of access they were entitled to.
 * </p>
 */
export const dayAfter = (date: string): string => {
  if (date === '') {
    return '';
  }

  const [year, month, day] = date.split('-').map(Number);

  // UTC on purpose: a local Date at midnight shifts by a day in half the world's timezones, and
  // this function exists to add exactly one.
  const next = new Date(Date.UTC(year, month - 1, day + 1));

  return next.toISOString().slice(0, 10);
};

/**
 * Whether an acknowledgement is still outstanding.
 *
 * <p>
 * Re-consent is required when the date <i>moves</i>, because what was acknowledged was a set of
 * bookings for a particular day. Ticking the box for the 30th and then changing the date to the
 * 12th would otherwise carry the tick across to three weeks of somebody else's plans.
 * </p>
 */
export const needsAcknowledgement = (hasImpact: boolean, acknowledgedFor: string | undefined, chosen: string): boolean =>
  hasImpact && acknowledgedFor !== chosen;

/**
 * A `yyyy-MM-dd` office date, written the way the reader's language writes dates.
 *
 * <p>
 * Parsed as UTC and formatted in UTC, which is the whole point: `new Date('2026-10-01')` is
 * midnight UTC, and rendering that in a timezone behind UTC prints the 30th of September. The
 * value is a calendar day at an office, so no local instant should ever be derived from it.
 * </p>
 */
export const formatOfficeDate = (date: string, language: string): string => {
  if (date === '') {
    return '';
  }

  const [year, month, day] = date.split('-').map(Number);

  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(language, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

/**
 * Whether a row in the access list is scheduled to lose access.
 *
 * <p>
 * Two states, not three. An <i>applied</i> access end is deliberately absent: applying one removes
 * the person's <c>user</c> role, and the list only returns people who hold one, so an "access
 * ended" badge would be a branch that can never render. `accessEndAppliedAt` is still read here so
 * that a row somehow carrying it is reported as not-scheduled rather than offering a "call off"
 * the server would refuse.
 * </p>
 */
export type AccessEndStatus = 'none' | 'scheduled';

export const accessEndStatus = (row: { accessEndsOn?: string; accessEndAppliedAt?: string }): AccessEndStatus => {
  if (row.accessEndAppliedAt != null || row.accessEndsOn == null || row.accessEndsOn === '') {
    return 'none';
  }

  return 'scheduled';
};

/** Sorts after any real date, in both directions, without needing a second comparator. */
const NOT_LEAVING_SORT_KEY = '9999-12-31';

/**
 * Sort key for the access-ends column.
 *
 * <p>
 * People who are not leaving sort <b>after</b> everyone who is, in both directions, rather than
 * clumping at whichever end an empty string happens to land. Sorting this column is how an
 * administrator gathers the leavers together, and a sort that buried them under two pages of blanks
 * would not do that.
 * </p>
 */
export const accessEndSortValue = (row: { accessEndsOn?: string; accessEndAppliedAt?: string }): string => {
  if (accessEndStatus(row) !== 'scheduled') {
    return NOT_LEAVING_SORT_KEY;
  }

  return row.accessEndsOn ?? NOT_LEAVING_SORT_KEY;
};
